import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import {
  MODAL_TYPE,
  MODAL_RESULT,
  type CommonModalOptions,
  type ModalType,
  type ModalResult
} from '@/modules/miiaFw/types/components/commonModal'

export const useCommonModalStore = defineStore('commonModalStore', () => {
  const options = ref<CommonModalOptions>({
    size: 'sm',
    title: '応急危険度システム',
    body: '',
    okTitle: 'OK',
    cancelTitle: 'キャンセル',
    okOnly: false,
    autofocusButton: undefined,
    okColor: 'primary',
    centered: false
  })
  const isOpen = ref(false)
  const storeResolve = ref<((value: ModalResult | PromiseLike<ModalResult>) => void) | null>(null)
  const storeReject = ref<((value: ModalResult | PromiseLike<ModalResult>) => void) | null>(null)

  const size = computed(() => {
    return options.value.size
  })

  const title = computed(() => {
    return options.value.title
  })

  const body = computed(() => {
    return options.value.body
  })

  const okTitle = computed(() => {
    return options.value.okTitle
  })

  const cancelTitle = computed(() => {
    return options.value.cancelTitle
  })

  const okOnly = computed(() => {
    return options.value.okOnly
  })

  const autofocusButton = computed(() => {
    return options.value.autofocusButton
  })

  const okColor = computed(() => {
    return options.value.okColor
  })

  const centered = computed(() => {
    return options.value.centered
  })

  /**
   * 他のモーダルが閉じられるまで待つ関数
   * @param なし
   * @returns なし
   */
  const waitForModalToClose = async (): Promise<void> => {
    const maxRetries = 50 // 最大リトライ回数
    const interval = 100 // ポーリング間隔(ms)

    for (let i = 0; i < maxRetries; i++) {
      if (!isOpen.value) {
        return // `isOpen` が false になったら終了
      }
      await new Promise(resolve => setTimeout(resolve, interval))

      throw new Error('モーダルが閉じられるのを待機中にタイムアウトしました')
    }
  }

  const openAlertModal = async (
    _options: Omit<CommonModalOptions, 'okOnly'>
  ): Promise<ModalResult> => {
    // 他のモーダルの処理と干渉することを防ぐため、isOpenがfalseになるのを待つ
    try {
      await waitForModalToClose()
    } catch (err) {
      console.error(err)
    }
    isOpen.value = true
    options.value = _getOptions(_options, MODAL_TYPE.alert)
    return new Promise<ModalResult>((resolve, reject) => {
      storeResolve.value = resolve
      storeReject.value = reject
    })
  }

  const openConfirmModal = async (
    _options: Omit<CommonModalOptions, 'okOnly'>
  ): Promise<ModalResult> => {
    // 他のモーダルの処理と干渉することを防ぐため、isOpenがfalseになるのを待つ
    try {
      await waitForModalToClose()
    } catch (err) {
      console.error(err)
    }

    isOpen.value = true
    options.value = _getOptions(_options, MODAL_TYPE.confirm)
    return new Promise<ModalResult>((resolve, reject) => {
      storeResolve.value = resolve
      storeReject.value = reject
    })
  }

  const ok = () => {
    storeResolve.value?.(MODAL_RESULT.OK)
  }

  const cancel = () => {
    storeReject.value?.(MODAL_RESULT.CANCEL)
  }

  const _getOptions = (
    options: Omit<CommonModalOptions, 'okOnly'>,
    type: ModalType
  ): CommonModalOptions => {
    const { title, body, okTitle, cancelTitle, autofocusButton, size, okColor, centered } = options
    const _options = {
      size: size === undefined ? 'sm' : size,
      title: title === undefined ? '応急危険度システム' : title,
      body: body,
      okTitle: okTitle === undefined ? 'OK' : okTitle,
      cancelTitle: cancelTitle == undefined ? 'キャンセル' : cancelTitle,
      okOnly: type === MODAL_TYPE.alert ? true : false,
      autofocusButton: autofocusButton,
      okColor: okColor === undefined ? 'primary' : okColor,
      centered: centered ?? false
    }
    return _options
  }

  const reset = () => {
    options.value = {
      size: 'sm',
      title: '応急危険度システム',
      body: '',
      okTitle: 'OK',
      cancelTitle: 'キャンセル',
      okOnly: false,
      autofocusButton: undefined,
      okColor: 'primary',
      centered: false
    }
    isOpen.value = false
    storeResolve.value = null
    storeReject.value = null
  }

  return {
    options,
    isOpen,
    size,
    title,
    body,
    okTitle,
    cancelTitle,
    okOnly,
    autofocusButton,
    okColor,
    centered,
    openAlertModal,
    openConfirmModal,
    ok,
    cancel,
    reset
  }
})
